import { useMemo } from 'react';
import { useFindByQuery } from '../../graphql';
import { RoutineType } from '../../graphql/enums';
import { WorkspaceRoutine } from '../../routine';

/**
 * Hook to fetch embedded routines from workflow questions
 * @param directRoutines - The initial list of routines to scan for embedded routines
 * @returns Object containing routines with embedded routines and loading state
 */
export const useEmbeddedRoutines = (directRoutines: WorkspaceRoutine[]) => {
  // Extract embedded routine IDs from workflow questions
  const embeddedRoutineIds = useMemo(() => {
    if (!directRoutines || directRoutines.length === 0) {
      return [];
    }

    const workflowQuestionRoutineIds: string[] = directRoutines
      .filter((r) => r.type === RoutineType.Workflow)
      .flatMap((workflowRoutine) =>
        ((workflowRoutine.config as any)?.pages ?? []).flatMap((p: any) =>
          (p.questions ?? [])
            .filter((q: any) => q?.type === 'ROUTINE')
            .map((q: any) => q.workspaceRoutineId as string)
        )
      );

    // Unique IDs that are referenced in workflow questions but not already direct
    return Array.from(
      new Set(
        workflowQuestionRoutineIds.filter(
          (id) => id && !directRoutines.some((r) => r.id === id)
        )
      )
    );
  }, [directRoutines]);

  // Fetch embedded routines
  const { data: extraRoutinesData, isLoading: isEmbeddedRoutinesLoading } =
    useFindByQuery({
      modelName: 'workspaceroutine',
      query: FIND_WORKSPACE_ROUTINES,
      variables: {
        filter: embeddedRoutineIds.length
          ? { id: { in: embeddedRoutineIds } }
          : undefined,
      },
      options: { enabled: embeddedRoutineIds.length > 0 },
    });

  // Combine direct and embedded routines
  const routinesWithEmbedded = useMemo(() => {
    const extraRoutines = (
      (extraRoutinesData as { items?: WorkspaceRoutine[] } | undefined)?.items ??
      []
    ).filter((r) => r && !directRoutines.some((d) => d.id === r.id));

    return embeddedRoutineIds.length
      ? [...directRoutines, ...extraRoutines]
      : directRoutines;
  }, [directRoutines, extraRoutinesData, embeddedRoutineIds.length]);

  return {
    routinesWithEmbedded,
    isEmbeddedRoutinesLoading,
  };
};

const FIND_WORKSPACE_ROUTINES = `
  query FindWorkspaceRoutines($filter: WorkspaceRoutineFilter) {
    findWorkspaceRoutines(filter: $filter) {
      items {
        id
        name
        type
        order
        config
        currentVersion { id }
      }
    }
  }
`;
